import { type ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import WheelLoader from './components/common/WheelLoader'
import AppLayout from './components/layout/AppLayout'
import AuthenticatedLayout from './components/layout/AuthenticatedLayout'
import { useAuth } from './hooks/useAuth'
import AboutPage from './pages/AboutPage'
import AccountPage from './pages/AccountPage'
import BlogPage from './pages/BlogPage'
import ContactPage from './pages/ContactPage'
import CustomizePage from './pages/CustomizePage'
import DashboardPage from './pages/DashboardPage'
import EditDetailsPage from './pages/EditDetailsPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/LoginPage'
import PageEditorPage from './pages/PageEditorPage'
import PagesPage from './pages/PagesPage'
import PostEditorPage from './pages/PostEditorPage'
import PrivacyPage from './pages/PrivacyPage'
import ProfilePage from './pages/ProfilePage'
import RegisterPage from './pages/RegisterPage'
import TermsPage from './pages/TermsPage'
import { getUrlConfig } from './utils/url'

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="mx-auto mb-4">
          <WheelLoader width={100} />
        </div>
        <p className="text-black/60">Loading...</p>
      </div>
    </div>
  )
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) return <LoadingSpinner />
  if (!isAuthenticated) return <Navigate to="/login" replace />

  return <>{children}</>
}

function GuestRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth()

  if (loading) return <LoadingSpinner />
  if (isAuthenticated) return <Navigate to="/home" replace />

  return <>{children}</>
}

export default function AppRouter() {
  const { isSubdomain, subdomain } = getUrlConfig()

  // If on a profile subdomain, render ProfilePage directly
  if (isSubdomain && subdomain) {
    return <ProfilePage username={subdomain} />
  }

  // Main app routes
  return (
    <Routes>
      {/* Auth routes - no navbar (use their own AuthLayout) */}
      <Route
        path="/login"
        element={
          <GuestRoute>
            <LoginPage />
          </GuestRoute>
        }
      />
      <Route
        path="/register"
        element={
          <GuestRoute>
            <RegisterPage />
          </GuestRoute>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* App routes with navbar */}
      <Route element={<AppLayout />}>
        {/* Landing page - for guests only */}
        <Route path="/" element={<LandingPage />} />

        {/* Authenticated routes with sidebar layout */}
        <Route
          element={
            <ProtectedRoute>
              <AuthenticatedLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/home" element={<DashboardPage />} />
          <Route path="/edit" element={<EditDetailsPage />} />
          <Route path="/customize" element={<CustomizePage />} />
          <Route path="/theme" element={<Navigate to="/customize" replace />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/new" element={<PostEditorPage />} />
          <Route path="/blog/edit/:id" element={<PostEditorPage />} />
          <Route path="/pages" element={<PagesPage />} />
          <Route path="/pages/new" element={<PageEditorPage />} />
          <Route path="/pages/edit/:id" element={<PageEditorPage />} />
          <Route path="/account" element={<AccountPage />} />
        </Route>

        {/* Static pages */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />

        {/* Redirect old edit-profile route to new /edit route */}
        <Route path="/edit-profile" element={<Navigate to="/edit" replace />} />
      </Route>
    </Routes>
  )
}
